/**
 * A hosted agent's public demo page, as our team manages it: one link per agent, live for 90
 * days, paused or given another 90 from here. An agent built from a website gets its link (and
 * its colours) at the end of the build; one set up by hand gets it from the button below.
 *
 * IT ACTS AT ONCE, like Team: nothing here goes through the save bar. A link is something we
 * hand to a person, not a setting the next call reads.
 *
 * Under it, who opened the link (migration 0032): the loads of the page that count, and a
 * line for the ones that do not - link previews, scanners, and our own.
 */

import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError, post, useApi, type DemoLink, type DemoVisits } from "../../../api";
import { duration, when } from "../../../format";
import { Check, Copy } from "../../Icons";
import { Basis, Button, CardHead, SettingCard, SettingRow, StatusPill, TextInput, cx } from "../../../ui";

const STATE = {
  live: { tone: "good", word: "Live" },
  paused: { tone: "neutral", word: "Paused" },
  expired: { tone: "warm", word: "Ended" },
} as const;

export function DemoPagePane({ agentId, agentSlug }: { agentId: string; agentSlug: string }) {
  const { data, error, loading, reload } = useApi<{ link: DemoLink | null }>(`/v1/agent/demo-link?agent=${encodeURIComponent(agentId)}`);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  /** The slug being typed, while the address is being edited; null otherwise. */
  const [slug, setSlug] = useState<string | null>(null);

  async function act(
    path: "" | "/pause" | "/reopen" | "/logo" | "/logo/remove" | "/slug",
    extra: Record<string, unknown> = {},
  ): Promise<boolean> {
    setBusy(true);
    setFailure(null);
    try {
      await post(`/v1/agent/demo-link${path}`, { agent: agentId, ...extra });
      reload();
      return true;
    } catch (err) {
      setFailure(err instanceof ApiError ? err.message : "That did not work. Try again.");
      return false;
    } finally {
      setBusy(false);
    }
  }

  if (loading && !data) return <SettingCard className="p-6 text-sm text-ink-faint">Loading…</SettingCard>;
  if (error) return <SettingCard className="p-6 text-sm text-alert">{error.message}</SettingCard>;

  const link = data?.link ?? null;
  return (
    <>
      <SettingCard>
        {!link ? (
          <div className="grid gap-3 px-6 py-5.5">
            <p className="text-sm text-ink-soft">This agent has no demo page yet. Calls on it are filed as test calls.</p>
            <div>
              <Button variant="primary" disabled={busy} onClick={() => void act("")}>
                Create demo page
              </Button>
            </div>
          </div>
        ) : (
          <>
            <SettingRow
              first
              label="Link"
              help={
                slug === null
                  ? "Nobody can find it without being sent it. Calls on it are filed as test calls."
                  : "The old link stops working as soon as you save."
              }
            >
              {slug === null ? (
                <div className="flex flex-wrap items-center gap-2">
                  <a href={link.url} target="_blank" rel="noreferrer" className="min-w-0 truncate font-mono text-sm text-accent hover:underline">
                    {link.url}
                  </a>
                  <Button
                    onClick={() => {
                      void navigator.clipboard?.writeText(link.url);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1600);
                    }}
                  >
                    {copied ? <Check /> : <Copy />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                  <Button variant="ghost" onClick={() => setSlug(link.slug)}>
                    Edit
                  </Button>
                </div>
              ) : (
                <form
                  className="flex flex-wrap items-center gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void act("/slug", { slug }).then((saved) => saved && setSlug(null));
                  }}
                >
                  <span className="font-mono text-sm text-ink-faint">{link.url.slice(0, link.url.length - link.slug.length)}</span>
                  <TextInput
                    autoFocus
                    aria-label="Address"
                    value={slug}
                    maxLength={60}
                    spellCheck={false}
                    onChange={(event) => setSlug(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") setSlug(null);
                    }}
                    className="w-52 font-mono"
                  />
                  <Button type="submit" variant="primary" disabled={busy || slug.length < 3}>
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setSlug(null);
                      setFailure(null);
                    }}
                  >
                    Cancel
                  </Button>
                </form>
              )}
            </SettingRow>
            <SettingRow label="Status" help="A link closes on its own 90 days after it was opened.">
              <div className="flex flex-wrap items-center gap-3">
                <StatusPill tone={STATE[link.state].tone}>
                  {STATE[link.state].word}
                  {link.state === "live" && ` · ${link.daysLeft} ${link.daysLeft === 1 ? "day" : "days"} left`}
                </StatusPill>
                {link.state === "live" ? (
                  <Button disabled={busy} onClick={() => void act("/pause")}>
                    Pause
                  </Button>
                ) : (
                  <Button disabled={busy} onClick={() => void act("/reopen")} title="Reopening gives it another 90 days">
                    Reopen for 90 days
                  </Button>
                )}
              </div>
            </SettingRow>
            <SettingRow label="Logo" help="At the top of the page and on the phone. PNG, JPEG or WebP, under 512 KB.">
              <LogoPicker link={link} busy={busy} onPick={(image) => void act("/logo", { image })} onRemove={() => void act("/logo/remove")} />
            </SettingRow>
            <SettingRow label="Colours" help="From their website, when it had any. Otherwise ours.">
              <Swatches brand={link.brand} />
            </SettingRow>
          </>
        )}
        {failure && (
          <p role="alert" className="border-t border-line-soft px-6 py-3 text-sm text-alert">
            {failure}
          </p>
        )}
      </SettingCard>
      {link && <VisitsCard agentId={agentId} agentSlug={agentSlug} />}
    </>
  );
}

const VISIT_COLS = "grid-cols-[130px_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_70px_60px]";

/**
 * Who opened the link, newest first, fifty at a time. Times are the reader's own clock: a
 * visitor has no business hours to be read in.
 *
 * IT STAYS ONE CARD HIGH however busy the link is: the rows scroll inside it under a header
 * that stays put, and older pages are fetched on request and added below. The summary at the
 * top is over every visit, not the rows loaded so far.
 */
function VisitsCard({ agentId, agentSlug }: { agentId: string; agentSlug: string }) {
  const path = `/v1/agent/demo-link/visits?agent=${encodeURIComponent(agentId)}`;
  const { data, error } = useApi<DemoVisits>(path);
  // The older pages, tied to the first page they continue: a fresh first page starts over.
  const [more, setMore] = useState<{ after: DemoVisits | null; rows: DemoVisits["visits"]; next: string | null }>({
    after: null,
    rows: [],
    next: null,
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const continues = data !== null && more.after === data;
  const older = continues ? more.rows : [];
  const next = continues ? more.next : (data?.next ?? null);

  async function showOlder() {
    if (!next || !data) return;
    setLoadingMore(true);
    setMoreFailed(false);
    try {
      const page = await api<DemoVisits>(`${path}&before=${encodeURIComponent(next)}`);
      setMore({ after: data, rows: [...older, ...page.visits], next: page.next });
    } catch {
      setMoreFailed(true);
    } finally {
      setLoadingMore(false);
    }
  }

  if (error) return <SettingCard className="p-6 text-sm text-alert">{error.message}</SettingCard>;
  if (!data?.summary) return null;
  const { summary } = data;
  const visits = [...data.visits, ...older];

  const note = summary.visits
    ? `${summary.visits} ${summary.visits === 1 ? "visit" : "visits"} by ${summary.visitors} ${summary.visitors === 1 ? "person" : "people"}` +
      (summary.lastVisitAt ? ` · last ${when(summary.lastVisitAt)}` : "")
    : "Nobody has opened it yet.";
  const also = [
    ...Object.entries(summary.previews).map(([by, n]) => `previewed in ${by}${n > 1 ? ` ×${n}` : ""}`),
    summary.unconfirmed ? `${summary.unconfirmed} unconfirmed (likely a link scanner)` : null,
    summary.ours ? `${summary.ours} of ours` : null,
  ].filter(Boolean);

  return (
    <SettingCard flush>
      <CardHead title="Who opened it" note={note} />
      {visits.length > 0 && (
        <div className="max-h-[28rem] overflow-y-auto overscroll-contain" data-testid="visit-list">
          <div
            className={cx(
              "sticky top-0 z-10 grid gap-4 border-b border-line-soft bg-ground px-5 py-2.5 text-2xs font-semibold text-ink-faint max-[720px]:hidden",
              VISIT_COLS,
            )}
          >
            <span>When</span>
            <span>Where</span>
            <span>Network</span>
            <span>Device</span>
            <span>Stayed</span>
            <span>Called</span>
          </div>
          {visits.map((visit, index) => (
            <div
              key={visit.id}
              className={cx(
                "grid items-baseline gap-4 px-5 py-3 text-sm text-ink-soft",
                VISIT_COLS,
                index > 0 && "border-t border-line-soft",
                "max-[720px]:grid-cols-1 max-[720px]:gap-1",
              )}
            >
              <span className="nums text-ink">
                {when(visit.at)}
                {visit.returning && <Basis inline>again</Basis>}
              </span>
              <span className="truncate">{visit.place ?? "—"}</span>
              <span className="truncate">{visit.network ?? "—"}</span>
              <span className="truncate">{visit.device}</span>
              <span className="nums">{visit.seconds === null ? "—" : duration(visit.seconds * 1000)}</span>
              <span>
                {visit.call !== null ? (
                  <Link to={`/${agentSlug}/calls/${visit.call}?test=true`} className="font-medium text-accent hover:underline">
                    Call
                  </Link>
                ) : visit.calledAt !== null ? (
                  "Yes"
                ) : (
                  "—"
                )}
              </span>
            </div>
          ))}
          {(next || moreFailed) && (
            <div className="flex items-center gap-3 border-t border-line-soft px-5 py-3">
              <Button disabled={loadingMore} onClick={() => void showOlder()}>
                {loadingMore ? "Loading…" : "Show older visits"}
              </Button>
              <span className="text-xs text-ink-faint">
                {moreFailed ? "Those did not load. Try again." : `${visits.length} of ${summary.visits} shown`}
              </span>
            </div>
          )}
        </div>
      )}
      {also.length > 0 && (
        <p className="border-t border-line-soft px-5 py-3 text-xs text-ink-faint">Also {also.join(" · ")}.</p>
      )}
    </SettingCard>
  );
}

const MAX_LOGO_BYTES = 512 * 1024;

/**
 * The logo as the page shows it, and the controls to change it. The file is read here and
 * sent as a `data:` URL; the Worker checks its bytes, so the `accept` list is only a hint.
 */
function LogoPicker({
  link,
  busy,
  onPick,
  onRemove,
}: {
  link: DemoLink;
  busy: boolean;
  onPick: (image: string) => void;
  onRemove: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [tooBig, setTooBig] = useState(false);

  function read(file: File | undefined) {
    if (!file) return;
    setTooBig(file.size > MAX_LOGO_BYTES);
    if (file.size > MAX_LOGO_BYTES) return;
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === "string" && onPick(reader.result);
    reader.readAsDataURL(file);
  }

  // An uploaded logo is a `/v1/public/...` path on this same origin; the site's is its own URL.
  const src = link.brand.logoUrl;

  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-3">
        {src ? (
          <span className="grid h-14 min-w-20 place-items-center rounded-md border border-line bg-paper px-3">
            <img src={src} alt="Their logo" className="max-h-10 max-w-[160px] object-contain" />
          </span>
        ) : (
          <span className="text-sm text-ink-faint">No logo. The page shows their name.</span>
        )}
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="sr-only"
          aria-label="Choose a logo"
          onChange={(event) => {
            read(event.target.files?.[0]);
            event.target.value = "";
          }}
        />
        <Button disabled={busy} onClick={() => input.current?.click()}>
          {src ? "Replace" : "Upload logo"}
        </Button>
        {src && (
          <Button variant="ghost" disabled={busy} onClick={onRemove}>
            Remove
          </Button>
        )}
      </div>
      {link.logo === "site" && <span className="text-xs text-ink-faint">From their website.</span>}
      {tooBig && <span role="alert" className="text-xs text-alert">That file is over 512 KB. Use a smaller one.</span>}
    </div>
  );
}

function Swatches({ brand }: { brand: DemoLink["brand"] }) {
  const colours = [brand.primary, brand.primaryDeep, brand.accent, brand.wash].filter(Boolean) as string[];
  if (!colours.length) return <span className="text-sm text-ink-faint">Replystate's own.</span>;
  return (
    <div className="flex items-center gap-2">
      {colours.map((hex) => (
        <span key={hex} title={hex} className="size-6 rounded-full border border-line" style={{ background: hex }} />
      ))}
    </div>
  );
}
